/**
 * Admin 테이블 집합체 루트(Abstract).
 *
 * 서브클래스: ListingTable (PC 테이블 + 모바일 카드), CardTable (카드 전용)
 *
 * 공통 기능:
 *  - performAction(): AJAX (GET/POST/PATCH/DELETE) + Modal.confirm + Toast 피드백
 *  - bindActionButtons(): [data-row-action] 버튼 이벤트 위임
 *    → data-action-url    : RESTful 엔드포인트 (예: /console/site/works/123)
 *       data-action-method : GET | POST | PATCH | DELETE (기본 DELETE)
 *       data-action-confirm: 확인 메시지 (없으면 즉시 실행)
 *       data-success-action: "remove-row" → 행 DOM 제거
 *       data-success-toast / data-error-toast
 *  - removeRow(id): 서브클래스 구현 (DOM 제거)
 *  - checkEmpty(): 서브클래스 구현 (빈 상태 표시)
 *
 * RESTful 규칙: docs/03-constants/conventions.md §RESTful
 */

import { Toast } from "../../utils/Toast";
import { Modal } from "../../utils/Modal";

export interface RowActionOptions {
    confirm?: string;
    successToast?: string;
    errorToast?: string;
    onSuccess?: (data: Record<string, unknown>) => void;
    onError?: (msg: string) => void;
}

function csrfToken(): string {
    return (
        document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ??
        document.querySelector<HTMLInputElement>('input[name="_token"]')?.value ??
        ""
    );
}

export abstract class Table {
    protected readonly el: HTMLElement;

    constructor(el: HTMLElement) {
        this.el = el;
    }

    abstract init(): this;
    abstract removeRow(id: string): void;
    protected abstract checkEmpty(): void;

    /** AJAX 액션 실행. PUT/PATCH/DELETE 는 _method 필드로 HTML form 터널링. fetch 는 실제 메서드 사용. */
    async performAction(
        url: string,
        method: "POST" | "DELETE" | "PUT" | "PATCH",
        opts: RowActionOptions = {}
    ): Promise<void> {
        if (opts.confirm) {
            const ok = await Modal.confirm(opts.confirm);
            if (!ok) return;
        }

        const token = csrfToken();
        const fd = new FormData();
        if (token) fd.append("_token", token);
        if (method !== "POST") fd.append("_method", method);

        let data: Record<string, unknown> = {};
        try {
            const res = await fetch(url, {
                method: "POST",
                body: fd,
                headers: {
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                    ...(token ? { "X-CSRF-TOKEN": token } : {}),
                },
                credentials: "same-origin",
            });

            const text = await res.text();
            try { data = text ? (JSON.parse(text) as Record<string, unknown>) : {}; } catch { /* */ }

            if (!res.ok) {
                const msg = String(data.message ?? data.msg ?? opts.errorToast ?? "오류가 발생했습니다.");
                Toast.error(msg);
                opts.onError?.(msg);
                return;
            }

            const successMsg = opts.successToast ?? String(data.message ?? data.msg ?? "");
            if (successMsg) Toast.success(successMsg);
            opts.onSuccess?.(data);
        } catch {
            Toast.error("네트워크 오류가 발생했습니다.");
        }
    }

    /** [data-row-action] 버튼 이벤트 위임. */
    protected bindActionButtons(): void {
        this.el.addEventListener("click", (e) => {
            const btn = (e.target as Element).closest<HTMLElement>("[data-row-action]");
            if (!btn) return;
            e.preventDefault();

            const rowEl = btn.closest<HTMLElement>("[data-row-id], [data-id]");
            const id = rowEl?.dataset.rowId ?? rowEl?.dataset.id ?? "";
            const url = btn.dataset.actionUrl ?? "";
            const method = (btn.dataset.actionMethod ?? "DELETE") as "POST" | "DELETE" | "PUT" | "PATCH";
            const confirm = btn.dataset.actionConfirm;
            const successAction = btn.dataset.successAction;

            if (!url) return;

            void this.performAction(url, method, {
                confirm,
                successToast: btn.dataset.successToast,
                errorToast: btn.dataset.errorToast,
                onSuccess: (data) => {
                    if (successAction === "remove-row" && id) {
                        this.removeRow(id);
                    }
                    const redirect =
                        (data.redirect as string | undefined) ??
                        ((data.data as Record<string, unknown> | undefined)?.redirect as string | undefined);
                    if (redirect) window.location.href = redirect;
                },
            });
        });
    }
}
